import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CalendarClock, Activity, Save, Calendar, Repeat, Clock } from 'lucide-react';
import ApiService from '../../services/apiService';

// ── Helper: compile visual schedule into cron expression ─────────────────────
function compileCron({ frequency, selectedDays, hour, minute }) {
  const h = parseInt(hour, 10) || 0;
  const m = parseInt(minute, 10) || 0;

  if (frequency === 'once') {
    // "Once" runs daily — will be auto-deactivated after first execution
    return `${m} ${h} * * *`;
  }

  // Recurring: build day-of-week field
  const dayMap = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 0 };
  const cronDays = selectedDays.map(d => dayMap[d]).sort().join(',');
  return `${m} ${h} * * ${cronDays || '*'}`;
}

// ── Helper: parse cron expression back into visual schedule ──────────────────
function parseCron(cronStr) {
  if (!cronStr) return { frequency: 'recurring', selectedDays: [], hour: '8', minute: '0' };
  const parts = cronStr.trim().split(/\s+/);
  if (parts.length < 5) return { frequency: 'recurring', selectedDays: [], hour: '8', minute: '0' };

  const minute = parts[0];
  const hour = parts[1];
  const dayOfWeek = parts[4];

  const reverseMap = { 0: 'Sun', 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat' };

  if (dayOfWeek === '*') {
    return { frequency: 'once', selectedDays: [], hour, minute };
  }

  const selectedDays = dayOfWeek.split(',').map(d => reverseMap[parseInt(d, 10)]).filter(Boolean);
  return { frequency: 'recurring', selectedDays, hour, minute };
}

// ── Human-readable metric labels ─────────────────────────────────────────────
const METRIC_LABELS = {
  soil_moisture: 'Soil Moisture',
  soil_temp: 'Soil Temperature',
  soil_ph: 'Soil pH',
  nitrogen_n: 'Nitrogen (N)',
  phosphorus_p: 'Phosphorus (P)',
  potassium_k: 'Potassium (K)',
  salinity: 'Salinity',
  ec: 'EC Signal',
  ambient_temp: 'Ambient Temperature',
  ambient_humidity: 'Humidity',
};

// ── Day chip keys for the visual agenda ──────────────────────────────────────
const DAY_KEYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ══════════════════════════════════════════════════════════════════════════════
// SCHEDULE BUILDER MODAL
// ══════════════════════════════════════════════════════════════════════════════
const ScheduleBuilderModal = ({ isOpen, onClose, onSave, actuators, t, editingSchedule = null, userId }) => {
  const tc = t?.control || {};

  // ── Form state ──────────────────────────────────────────────────────────────
  const [triggerType, setTriggerType] = useState('cron');
  const [actuatorId, setActuatorId] = useState('');
  const [scheduleName, setScheduleName] = useState('');

  // Visual Schedule (replaces raw cron)
  const [frequency, setFrequency] = useState('recurring');
  const [selectedDays, setSelectedDays] = useState([]);
  const [scheduleHour, setScheduleHour] = useState('8');
  const [scheduleMinute, setScheduleMinute] = useState('0');

  // Sensor trigger params
  const [sensor, setSensor] = useState('');
  const [operator, setOperator] = useState('<');
  const [thresholdValue, setThresholdValue] = useState('');
  const [targetDeviceId, setTargetDeviceId] = useState('');

  // Execution Profile
  const [action, setAction] = useState('on');
  const [duration, setDuration] = useState('10');

  // Dynamic data from backend
  const [sensorDevices, setSensorDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);

  // ── Fetch sensor devices on open ────────────────────────────────────────────
  const fetchSensorDevices = useCallback(async () => {
    if (!userId) return;
    setLoadingDevices(true);
    try {
      const data = await ApiService.get(`/control/sensor-devices?user_id=${userId}`);
      setSensorDevices(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Fetch sensor devices error:', e);
    }
    setLoadingDevices(false);
  }, [userId]);

  // ── Initialize / reset form on open ─────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      fetchSensorDevices();

      if (editingSchedule) {
        setActuatorId(editingSchedule.actuator_id || '');
        setScheduleName(editingSchedule.schedule_name || '');
        setTriggerType(editingSchedule.trigger_type || 'cron');

        // Parse cron into visual schedule
        if (editingSchedule.cron_expression) {
          const parsed = parseCron(editingSchedule.cron_expression);
          setFrequency(parsed.frequency);
          setSelectedDays(parsed.selectedDays);
          setScheduleHour(parsed.hour);
          setScheduleMinute(parsed.minute);
        }

        const payload = typeof editingSchedule.action_payload === 'string'
          ? JSON.parse(editingSchedule.action_payload)
          : (editingSchedule.action_payload || {});
        setAction(payload.action || 'on');
        setDuration(payload.duration_minutes || '');

        const threshold = typeof editingSchedule.threshold_config === 'string'
          ? JSON.parse(editingSchedule.threshold_config)
          : (editingSchedule.threshold_config || {});
        setSensor(threshold.sensor || '');
        setOperator(threshold.operator || '<');
        setThresholdValue(threshold.value || '');
        setTargetDeviceId(threshold.device_id || '');
      } else {
        setActuatorId(actuators?.[0]?.id || '');
        setScheduleName('');
        setTriggerType('cron');
        setFrequency('recurring');
        setSelectedDays([]);
        setScheduleHour('8');
        setScheduleMinute('0');
        setSensor('');
        setOperator('<');
        setThresholdValue('');
        setTargetDeviceId('');
        setAction('on');
        setDuration('10');
      }
    }
  }, [isOpen, editingSchedule, actuators, fetchSensorDevices]);

  // ── Get available metrics for selected device ───────────────────────────────
  const selectedDeviceMetrics = sensorDevices.find(d => String(d.id) === String(targetDeviceId))?.metrics || [];

  // Reset metric when device changes
  useEffect(() => {
    if (targetDeviceId && selectedDeviceMetrics.length > 0 && !selectedDeviceMetrics.includes(sensor)) {
      setSensor(selectedDeviceMetrics[0]);
    }
  }, [targetDeviceId, selectedDeviceMetrics, sensor]);

  // ── Day chip toggle ─────────────────────────────────────────────────────────
  const toggleDay = (day) => {
    setSelectedDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  if (!isOpen) return null;

  // ── Submit handler ──────────────────────────────────────────────────────────
  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      actuator_id: parseInt(actuatorId),
      schedule_name: scheduleName || `${triggerType} Rule`,
      trigger_type: triggerType,
      is_active: true,
      action_payload: {
        action,
        ...(duration ? { duration_minutes: parseInt(duration) } : {})
      }
    };

    if (triggerType === 'cron') {
      payload.cron_expression = compileCron({ frequency, selectedDays, hour: scheduleHour, minute: scheduleMinute });
    } else {
      payload.threshold_config = {
        sensor,
        operator,
        value: parseFloat(thresholdValue),
        device_id: targetDeviceId ? parseInt(targetDeviceId) : null
      };
    }

    onSave(payload, editingSchedule?.id);
  };

  // ── Day label from translation dict ─────────────────────────────────────────
  const dayLabel = (key) => {
    const map = { Mon: tc.dayMon, Tue: tc.dayTue, Wed: tc.dayWed, Thu: tc.dayThu, Fri: tc.dayFri, Sat: tc.daySat, Sun: tc.daySun };
    return map[key] || key;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#0A0F1E]/60 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto my-auto bg-[#F4EFEA] dark:bg-[#0A0F1E] rounded-3xl shadow-2xl border border-white/20 dark:border-white/10"
      >
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex justify-between items-center p-5 border-b border-[#1B263B]/5 dark:border-white/5 bg-white/50 dark:bg-white/5 sticky top-0 z-10 rounded-t-3xl backdrop-blur-md">
          <h2 className="text-lg font-bold flex items-center gap-2">
            {editingSchedule ? (tc.editSchedule || 'Edit Schedule') : (tc.createSchedule || 'Create Schedule')}
          </h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* ── Form ────────────────────────────────────────────────────────── */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Rule Name + Target Actuator */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-[#1B263B]/60 dark:text-white/50 mb-1">
                {tc.targetActuator || 'Target Actuator'}
              </label>
              <select
                required
                value={actuatorId}
                onChange={e => setActuatorId(e.target.value)}
                className="w-full bg-white dark:bg-[#1B263B] border border-[#1B263B]/10 dark:border-white/10 rounded-xl px-4 py-2.5 text-sm"
              >
                <option value="">{tc.selectActuator || 'Select Actuator'}</option>
                {actuators.map(a => <option key={a.id} value={a.id}>{a.device_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-[#1B263B]/60 dark:text-white/50 mb-1">
                {tc.ruleName || 'Rule Name'}
              </label>
              <input
                required
                value={scheduleName}
                onChange={e => setScheduleName(e.target.value)}
                placeholder={tc.ruleNamePlaceholder || 'e.g. Morning Watering'}
                className="w-full bg-white dark:bg-[#1B263B] border border-[#1B263B]/10 dark:border-white/10 rounded-xl px-4 py-2.5 text-sm"
              />
            </div>
          </div>

          {/* ── Trigger Type Tabs ─────────────────────────────────────────── */}
          <div className="flex bg-[#1B263B]/5 dark:bg-white/5 p-1 rounded-xl">
            <button
              type="button"
              onClick={() => setTriggerType('cron')}
              className={`flex-1 flex justify-center items-center gap-2 py-2 text-sm font-bold rounded-lg transition-colors ${triggerType === 'cron' ? 'bg-white dark:bg-[#6B8F71] shadow-sm text-black dark:text-white' : 'text-[#1B263B]/60 dark:text-white/50 hover:text-[#C47A2C]'}`}
            >
              <CalendarClock size={16} /> {tc.timeSchedule || 'Time Schedule'}
            </button>
            <button
              type="button"
              onClick={() => setTriggerType('threshold')}
              className={`flex-1 flex justify-center items-center gap-2 py-2 text-sm font-bold rounded-lg transition-colors ${triggerType === 'threshold' ? 'bg-white dark:bg-[#6B8F71] shadow-sm text-black dark:text-white' : 'text-[#1B263B]/60 dark:text-white/50 hover:text-[#C47A2C]'}`}
            >
              <Activity size={16} /> {tc.sensorTrigger || 'Sensor Trigger'}
            </button>
          </div>

          {/* ══════════════════════════════════════════════════════════════ */}
          {/* TIME SCHEDULE — Visual Agenda Selector                       */}
          {/* ══════════════════════════════════════════════════════════════ */}
          {triggerType === 'cron' ? (
            <div className="space-y-5">
              {/* Level 1: Frequency */}
              <div>
                <label className="block text-xs font-bold text-[#1B263B]/60 dark:text-white/50 mb-2">
                  <Repeat size={13} className="inline mr-1" />
                  {tc.selectDays || 'Frequency'}
                </label>
                <div className="flex gap-2">
                  {['once', 'recurring'].map(freq => (
                    <motion.button
                      key={freq}
                      type="button"
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => { setFrequency(freq); if (freq === 'once') setSelectedDays([]); }}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all duration-300
                        ${frequency === freq
                          ? 'bg-gradient-to-r from-[#C47A2C] to-[#A36322] text-white shadow-[0_0_20px_rgba(196,122,44,0.3)]'
                          : 'bg-white/60 dark:bg-white/5 text-[#1B263B]/50 dark:text-white/40 border border-[#1B263B]/10 dark:border-white/10'
                        }`}
                    >
                      {freq === 'once' ? (
                        <><Calendar size={14} className="inline mr-1.5" />{tc.frequencyOnce || 'Once'}</>
                      ) : (
                        <><Repeat size={14} className="inline mr-1.5" />{tc.frequencyRecurring || 'Recurring'}</>
                      )}
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Level 2: Days (Recurring) or Date (Once) */}
              <AnimatePresence mode="wait">
                {frequency === 'recurring' ? (
                  <motion.div
                    key="days"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                  >
                    <label className="block text-xs font-bold text-[#1B263B]/60 dark:text-white/50 mb-2">
                      {tc.selectDays || 'Select Days'}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {DAY_KEYS.map(day => {
                        const isSelected = selectedDays.includes(day);
                        return (
                          <motion.button
                            key={day}
                            type="button"
                            whileHover={{ scale: 1.08 }}
                            whileTap={{ scale: 0.92 }}
                            onClick={() => toggleDay(day)}
                            className={`agenda-day-chip w-[calc(25%-6px)] sm:w-auto sm:flex-1 py-2.5 rounded-xl text-xs font-bold transition-all duration-300
                              ${isSelected
                                ? 'bg-[#6B8F71] text-white shadow-[0_0_16px_rgba(107,143,113,0.4)]'
                                : 'bg-white/60 dark:bg-white/5 text-[#1B263B]/50 dark:text-white/40 border border-[#1B263B]/10 dark:border-white/10 hover:border-[#6B8F71]/40'
                              }`}
                          >
                            {dayLabel(day)}
                          </motion.button>
                        );
                      })}
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="date"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                  >
                    <label className="block text-xs font-bold text-[#1B263B]/60 dark:text-white/50 mb-2">
                      {tc.selectDate || 'Select Date'}
                    </label>
                    <p className="text-[0.65rem] text-[#1B263B]/40 dark:text-white/30 mb-1 italic">
                      {frequency === 'once' ? (tc.frequencyOnce || 'This rule will run once at the specified time and then auto-deactivate.') : ''}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Level 3: Time Picker */}
              <div>
                <label className="block text-xs font-bold text-[#1B263B]/60 dark:text-white/50 mb-2">
                  <Clock size={13} className="inline mr-1" />
                  {tc.selectTime || 'Select Time'}
                </label>
                <div className="flex gap-3 items-center">
                  <div className="flex-1">
                    <label className="block text-[0.6rem] font-semibold text-[#1B263B]/40 dark:text-white/30 mb-1 uppercase tracking-wide">
                      {tc.hour || 'Hour'}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="23"
                      value={scheduleHour}
                      onChange={e => setScheduleHour(e.target.value)}
                      className="w-full bg-white dark:bg-[#1B263B] border border-[#1B263B]/10 dark:border-white/10 rounded-xl px-4 py-2.5 text-sm text-center font-mono"
                    />
                  </div>
                  <span className="text-2xl font-bold text-[#C47A2C] mt-4">:</span>
                  <div className="flex-1">
                    <label className="block text-[0.6rem] font-semibold text-[#1B263B]/40 dark:text-white/30 mb-1 uppercase tracking-wide">
                      {tc.minute || 'Minute'}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={scheduleMinute}
                      onChange={e => setScheduleMinute(e.target.value)}
                      className="w-full bg-white dark:bg-[#1B263B] border border-[#1B263B]/10 dark:border-white/10 rounded-xl px-4 py-2.5 text-sm text-center font-mono"
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* ══════════════════════════════════════════════════════════════ */
            /* SENSOR TRIGGER — Dynamic Dropdowns                           */
            /* ══════════════════════════════════════════════════════════════ */
            <div className="space-y-4">
              {/* Source Device Dropdown */}
              <div>
                <label className="block text-xs font-bold text-[#1B263B]/60 dark:text-white/50 mb-1">
                  {tc.sourceDevice || 'Source Device'}
                </label>
                <select
                  required
                  value={targetDeviceId}
                  onChange={e => setTargetDeviceId(e.target.value)}
                  disabled={loadingDevices}
                  className="w-full bg-white dark:bg-[#1B263B] border border-[#1B263B]/10 dark:border-white/10 rounded-xl px-4 py-2.5 text-sm disabled:opacity-50"
                >
                  <option value="">{loadingDevices ? '...' : (tc.selectDevice || 'Select Device')}</option>
                  {sensorDevices.map(d => (
                    <option key={d.id} value={d.id}>{d.device_name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {/* Metric Dropdown */}
                <div className="col-span-1">
                  <label className="block text-xs font-bold text-[#1B263B]/60 dark:text-white/50 mb-1">
                    {tc.metric || 'Metric'}
                  </label>
                  <select
                    required
                    value={sensor}
                    onChange={e => setSensor(e.target.value)}
                    disabled={!targetDeviceId}
                    className="w-full bg-white dark:bg-[#1B263B] border border-[#1B263B]/10 dark:border-white/10 rounded-xl px-3 py-2.5 text-sm disabled:opacity-50"
                  >
                    <option value="">{tc.selectMetric || 'Select'}</option>
                    {selectedDeviceMetrics.map(m => (
                      <option key={m} value={m}>{METRIC_LABELS[m] || m}</option>
                    ))}
                  </select>
                </div>

                {/* Operator */}
                <div className="col-span-1">
                  <label className="block text-xs font-bold text-[#1B263B]/60 dark:text-white/50 mb-1">
                    {tc.operator || 'Operator'}
                  </label>
                  <select
                    value={operator}
                    onChange={e => setOperator(e.target.value)}
                    className="w-full bg-white dark:bg-[#1B263B] border border-[#1B263B]/10 dark:border-white/10 rounded-xl px-4 py-2.5 text-sm"
                  >
                    <option value="<">{'<'}</option>
                    <option value=">">{'>'}</option>
                    <option value="<=">{'≤'}</option>
                    <option value=">=">{'≥'}</option>
                    <option value="==">{'=='}</option>
                  </select>
                </div>

                {/* Threshold Value */}
                <div className="col-span-1">
                  <label className="block text-xs font-bold text-[#1B263B]/60 dark:text-white/50 mb-1">
                    {tc.threshold || 'Threshold'}
                  </label>
                  <input
                    required
                    type="number"
                    step="0.1"
                    value={thresholdValue}
                    onChange={e => setThresholdValue(e.target.value)}
                    placeholder="e.g. 40"
                    className="w-full bg-white dark:bg-[#1B263B] border border-[#1B263B]/10 dark:border-white/10 rounded-xl px-4 py-2.5 text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ── Execution Profile ──────────────────────────────────────────── */}
          <div className="bg-[#C47A2C]/5 rounded-xl p-4 border border-[#C47A2C]/10">
            <h3 className="text-xs font-bold text-[#C47A2C] mb-3">
              {tc.executionProfile || 'Execution Profile'}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-[#1B263B]/60 dark:text-white/50 mb-1">
                  {tc.action || 'Action'}
                </label>
                <select
                  value={action}
                  onChange={e => setAction(e.target.value)}
                  className="w-full bg-white/50 dark:bg-[#1B263B] border border-[#1B263B]/10 dark:border-white/10 rounded-xl px-4 py-2 text-sm"
                >
                  <option value="on">{tc.turnOn || 'Turn ON'}</option>
                  <option value="off">{tc.turnOff || 'Turn OFF'}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-[#1B263B]/60 dark:text-white/50 mb-1">
                  {tc.runDuration || 'Run Duration (mins)'}
                </label>
                <input
                  type="number"
                  min="1"
                  value={duration}
                  onChange={e => setDuration(e.target.value)}
                  placeholder="e.g. 10"
                  className="w-full bg-white/50 dark:bg-[#1B263B] border border-[#1B263B]/10 dark:border-white/10 rounded-xl px-4 py-2 text-sm"
                />
              </div>
            </div>
          </div>

          {/* ── Submit ──────────────────────────────────────────────────────── */}
          <div className="pt-2">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              className="w-full py-3 rounded-xl bg-gradient-to-r from-[#6B8F71] to-[#8FA994] text-white font-bold shadow-lg flex justify-center items-center gap-2"
            >
              <Save size={18} /> {tc.saveSchedule || 'Save Rule'}
            </motion.button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default ScheduleBuilderModal;
